import React from 'react';
import ContentLoader from 'react-content-loader';
import { useLoaderData } from 'react-router-dom';

import Descriptor from '@r6digital/rapidstor-types/react/entities/Descriptor';
import Location from '@r6digital/rapidstor-types/react/entities/Location';
import UnitType from '@r6digital/rapidstor-types/react/entities/UnitType';
import Deal from '@r6digital/rapidstor-types/types/Deal';

import { descriptorDimensions } from '@/utils/rapidstor';

import Inclusions from '@/components/descriptor/inclusion';
import DescriptorTag from '@/components/descriptor/tag';
import Option from '@/components/unit-option';

import { descriptorTitle } from '@/features/rapidstor/helpers';

import classes from './styles.module.scss';

interface Props {
    descriptor: Descriptor | undefined;
    unitType: UnitType | undefined;
    deal?: Deal;
}

export default function WizardDescriptor({ descriptor, unitType, deal }: Props) {
    const location = useLoaderData() as Location;

    return (
        <div className={classes.container}>
            <div className={classes.image}>
                {descriptor ? (
                    <img src={descriptor.picture ?? null} />
                ) : (
                    <ContentLoader
                        foregroundColor={`${location.skin.mainColour}`}
                        backgroundColor='#fff'
                        width={'240px'}
                        height={'100%'}
                    >
                        <rect x='0' y='0' width='100%' height='80%' />
                    </ContentLoader>
                )}
            </div>
            <div className={classes.descriptor_info}>
                {descriptor && unitType ? (
                    <>
                        <div className={`${classes.top_section}`}>
                            <div className={`${classes.first}`}>
                                <span className={`${classes.title}`}>{descriptorTitle(descriptor, location)[1]}</span>
                                <span className={classes.descriptor_size}>
                                    {descriptorDimensions(unitType, location)}
                                </span>
                            </div>
                            <div className={`${classes.last}`}>
                                {descriptor.tags && (
                                    <DescriptorTag
                                        wizard
                                        tags={descriptor.tags.slice(0, 2)}
                                        tertiary
                                    />
                                )}
                            </div>
                        </div>
                        <div className={`${classes.inclusions}`}>
                            {descriptor.descriptions && (
                                <Inclusions inclusions={descriptor.descriptions} />
                            )}
                        </div>
                        <span className={classes.descriptor_name}>{descriptor.name}</span>
                        <p className={classes.descriptor_description}>
                            {descriptor.description}
                        </p>
                    </>
                ) : (
                    <ContentLoader
                        foregroundColor={`${location.skin.mainColour}`}
                        backgroundColor='#fff'
                        height={'100%'}
                        width={'100%'}
                    >
                        <rect x='0' y='4' rx='3' ry='3' width='100%' height='30' />
                        <rect x='0' y='40' rx='3' ry='3' width='30%' height='16' />
                        <rect x='0' y='66' rx='3' ry='3' width='100%' height='20' />
                        <rect x='0' y='96' rx='3' ry='3' width='100%' height='16' />
                    </ContentLoader>
                )}
            </div>
            <div className={classes.descriptor_content}>
                {descriptor && unitType ? (
                    <>
                        <div className={`${classes.inclusions}`}>
                            {(descriptor.descriptions || deal) && (
                                <Inclusions
                                    inclusions={
                                        deal ? deal.descriptions : descriptor.descriptions
                                    }
                                />
                            )}
                        </div>
                        <div className={`${classes.tags}`}>
                            {descriptor.tags && (
                                <DescriptorTag wizard tags={descriptor.tags} tertiary />
                            )}
                        </div>
                        <div className={`${classes.option}`}>
                            <Option
                                option={unitType}
                                dealObject={deal}
                                desktopOption={true}
                                location={location}
                                selected={true}
                                initialRate={unitType.rates.rate}
                                showTotalPrice
                                optionText={descriptor?.jblOptionText}
                                wizard
                                setSelectedID={() => {
                                    return;
                                }}
                                theme={`base`}
                            />
                        </div>
                    </>
                ) : (
                    <ContentLoader
                        foregroundColor={`${location.skin.mainColour}`}
                        backgroundColor='#fff'
                        height={'230px'}
                        width={'100%'}
                    >
                        <rect x='0' y='4' rx='3' ry='3' width='20%' height='14' />
                        <rect x='0' y='24' rx='3' ry='3' width='100%' height='16' />
                        <rect x='0' y='46' rx='3' ry='3' width='100%' height='16' />

                        <rect x='0' y='96' rx='3' ry='3' width='80px' height='30' />
                        <rect x='90' y='96' rx='3' ry='3' width='80px' height='30' />
                        <rect x='180' y='96' rx='3' ry='3' width='80px' height='30' />

                        <rect x='0' y='138' rx='3' ry='3' width='100%' height='46' />
                    </ContentLoader>
                )}
            </div>
        </div>
    );
}
